// src/components/customers/JobsTable.jsx
import { Link } from "react-router-dom";
import { useEffect } from "react";

/**
 * JobsTable Component
 * Displays jobs in a responsive table with address information
 * Features mobile-optimized card view and desktop table view
 * No external icon dependencies - uses emojis and CSS for visual elements
 */
const JobsTable = ({
  jobs,
  onEditJob,
  onDeleteJob,
  customerId,
  highlightJobId,
}) => {
  // Scroll to highlighted job when component loads
  useEffect(() => {
    if (highlightJobId) {
      const element = document.getElementById(`job-${highlightJobId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    }
  }, [highlightJobId]);

  return (
    <>
      {/* Desktop Table View - Hidden on mobile */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                Date
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                Address
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                Postcode
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                Plan
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                Products
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">
                Requirements
              </th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job, index) => (
              <tr
                key={job.id}
                id={`job-${job.id}`}
                className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  highlightJobId === job.id
                    ? "bg-blue-50 border-blue-200 ring-1 ring-blue-200"
                    : index % 2 === 0
                    ? "bg-white"
                    : "bg-gray-50/50"
                }`}
              >
                {/* Date */}
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">📅</span>
                    <span className="font-medium text-gray-900">
                      {job.date}
                    </span>
                  </div>
                </td>

                {/* Address */}
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 flex-shrink-0">📍</span>
                    <span
                      className="text-gray-700 truncate max-w-xs"
                      title={job.address}
                    >
                      {job.address || "No address specified"}
                    </span>
                  </div>
                </td>

                {/* Postcode */}
                <td className="py-4 px-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {job.postcode || "N/A"}
                  </span>
                </td>

                {/* Plan */}
                <td className="py-4 px-4">
                  <p
                    className="text-sm text-gray-600 truncate max-w-xs"
                    title={job.plan}
                  >
                    {job.plan || "No plan specified"}
                  </p>
                </td>

                {/* Products */}
                <td className="py-4 px-4">
                  <p
                    className="text-sm text-gray-600 truncate max-w-xs"
                    title={job.products}
                  >
                    {job.products || "No products specified"}
                  </p>
                </td>

                {/* Requirements Summary */}
                <td className="py-4 px-4 text-center">
                  <div className="flex justify-center gap-1">
                    {job.hasUnderlay && (
                      <span
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded"
                        title="Has Underlay"
                      >
                        U
                      </span>
                    )}
                    {job.hasGrippers && (
                      <span
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded"
                        title="Has Grippers"
                      >
                        G
                      </span>
                    )}
                    {job.floorIsGood && (
                      <span
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded"
                        title="Floor is Good"
                      >
                        F
                      </span>
                    )}
                    {job.concrete && (
                      <span
                        className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded"
                        title="Concrete"
                      >
                        C
                      </span>
                    )}
                    {job.doorsNeedCutting > 0 && (
                      <span
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                        title={`${job.doorsNeedCutting} Doors`}
                      >
                        D{job.doorsNeedCutting}
                      </span>
                    )}
                  </div>
                </td>

                {/* Actions */}
                <td className="py-4 px-4">
                  <div className="flex justify-end gap-2">
                    <Link
                      to={`/customers/${customerId}/jobs/${job.id}/rooms`}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </Link>
                    <button
                      onClick={() => onEditJob(job)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Edit Job"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => onDeleteJob(job)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Job"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Visible only on mobile */}
      <div className="lg:hidden space-y-4">
        {jobs.map((job) => (
          <div
            key={job.id}
            id={`job-${job.id}`}
            className={`border rounded-lg p-4 space-y-3 hover:shadow-md transition-shadow ${
              highlightJobId === job.id
                ? "bg-blue-50 border-blue-200 ring-1 ring-blue-200"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Date Header */}
            <div className="flex items-center gap-2 text-gray-900 font-semibold text-base">
              <span>📅</span>
              <span>{job.date}</span>
            </div>

            {/* Address and Postcode */}
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 flex-shrink-0">📍</span>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    {job.address || "No address specified"}
                  </p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                    {job.postcode || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Plan and Products */}
            {(job.plan || job.products) && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                {job.plan && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      Plan:
                    </p>
                    <p className="text-sm text-gray-700">{job.plan}</p>
                  </div>
                )}
                {job.products && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                      <span>📦</span>
                      Products:
                    </p>
                    <p className="text-sm text-gray-700">{job.products}</p>
                  </div>
                )}
              </div>
            )}

            {/* Requirements */}
            {(job.hasUnderlay ||
              job.hasGrippers ||
              job.floorIsGood ||
              job.concrete ||
              job.doorsNeedCutting > 0 ||
              job.numberOfDoorPlateNeeded > 0) && (
              <div className="flex flex-wrap gap-1 pt-2">
                {job.hasUnderlay && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                    Underlay
                  </span>
                )}
                {job.hasGrippers && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                    Grippers
                  </span>
                )}
                {job.floorIsGood && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                    Good Floor
                  </span>
                )}
                {job.concrete && (
                  <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded">
                    Concrete
                  </span>
                )}
                {job.doorsNeedCutting > 0 && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                    {job.doorsNeedCutting} Door
                    {job.doorsNeedCutting > 1 ? "s" : ""} to cut
                  </span>
                )}
                {job.numberOfDoorPlateNeeded > 0 && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                    {job.numberOfDoorPlateNeeded} Door plate
                    {job.numberOfDoorPlateNeeded > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}

            {/* Action Buttons - Full width, touch-friendly */}
            <div className="flex gap-2 pt-3 border-t border-gray-200 mt-3">
              <Link
                to={`/customers/${customerId}/jobs/${job.id}/rooms`}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg
                  className="w-4 h-4 mr-1.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                View Details
              </Link>
              <button
                onClick={() => onEditJob(job)}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                title="Edit Job"
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              <button
                onClick={() => onDeleteJob(job)}
                className="inline-flex items-center justify-center px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                title="Delete Job"
              >
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default JobsTable;
